
import React, { useEffect, useRef, useState } from "react";
import "./Products.css";

import {
  FaSearch,
  FaTools,
  FaFaucet,
  FaStar,
  FaDoorOpen,
} from "react-icons/fa";

const API_URL =
  "https://etoile-bricolage-api.imanehibouzid2001.workers.dev";

const PRODUCTS_PER_PAGE = 12;

const categoryConfig = [
  { name: "Quincaillerie", title: "QUINCAILLERIE", icon: FaDoorOpen },
  { name: "Outils", title: "OUTILS", icon: FaTools },
  { name: "Sanitaires", title: "SANITAIRES", icon: FaFaucet },
  { name: "Marque Etoile", title: "MARQUE ÉTOILE", icon: FaStar },
];

const normalize = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

function Products() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] =
    useState("Quincaillerie");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const resultsRef = useRef(null);

  useEffect(() => {
    const controller = new AbortController();

    async function loadProducts() {
      try {
        setLoading(true);
        setError("");

        const response = await fetch(
          `${API_URL}/api/products`,
          { signal: controller.signal }
        );

        const data = await response.json();

        if (!response.ok || !data.success) {
          throw new Error(
            data.error || "Impossible de récupérer les produits."
          );
        }

        setProducts(
          Array.isArray(data.products) ? data.products : []
        );
      } catch (err) {
        if (err.name === "AbortError") return;

        console.error("Erreur chargement produits :", err);
        setError(
          "Impossible de charger les produits pour le moment."
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }

    loadProducts();
    return () => controller.abort();
  }, []);

  const searchTerm = normalize(search);

  // Recherche dans toutes les catégories
  const matchingProducts = products.filter((product) => {
    const searchableText = normalize(
      [
        product.name,
        product.description,
        product.category_name,
      ].join(" ")
    );

    return searchableText.includes(searchTerm);
  });

  // Compteur par catégorie
  const categories = categoryConfig.map((category) => ({
    ...category,
    count: matchingProducts.filter(
      (product) =>
        normalize(product.category_name) ===
        normalize(category.name)
    ).length,
  }));

  // En recherche, masquer les onglets sans résultat
  const visibleCategories = searchTerm
    ? categories.filter((category) => category.count > 0)
    : categories;

  // Sélectionner automatiquement la première catégorie
  // contenant des résultats si la catégorie actuelle est vide
  const selectedCategory =
    visibleCategories.find(
      (category) => category.name === activeCategory
    ) || visibleCategories[0];

  const selectedName = selectedCategory?.name;

  const categoryProducts = matchingProducts.filter(
    (product) =>
      normalize(product.category_name) ===
      normalize(selectedName)
  );

  const totalPages = Math.ceil(
    categoryProducts.length / PRODUCTS_PER_PAGE
  );

  const safePage = Math.min(
    currentPage,
    Math.max(totalPages, 1)
  );

  const displayedProducts = categoryProducts.slice(
    (safePage - 1) * PRODUCTS_PER_PAGE,
    safePage * PRODUCTS_PER_PAGE
  );

  const handleSearch = (value) => {
    setSearch(value);
    setCurrentPage(1);
  };

  const handleCategoryChange = (name) => {
    setActiveCategory(name);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);

    // Revenir au début des produits
    requestAnimationFrame(() => {
      resultsRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  // Pagination compacte : 1 2 3 ... 9
  const getPageNumbers = () => {
    const pages = [];

    for (let page = 1; page <= totalPages; page++) {
      if (
        page === 1 ||
        page === totalPages ||
        Math.abs(page - safePage) <= 1
      ) {
        pages.push(page);
      }
    }

    const result = [];

    pages.forEach((page, index) => {
      if (index > 0 && page - pages[index - 1] > 1) {
        result.push("...");
      }
      result.push(page);
    });

    return result;
  };

  return (
    <main>
      <section className="products-section">
        <h1 className="products-title">Nos Produits</h1>

        <p className="products-subtitle">
          Découvrez notre sélection de produits pour tous vos projets.
        </p>

        {/* RECHERCHE */}
        <div className="search-bar" role="search">
          <FaSearch className="search-icon" />

          <input
            type="search"
            placeholder="Rechercher un produit ou une catégorie..."
            aria-label="Rechercher un produit"
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
          />

          {search && (
            <button
              type="button"
              className="clear-search"
              onClick={() => handleSearch("")}
              aria-label="Effacer la recherche"
            >
              ×
            </button>
          )}
        </div>

        {loading && (
          <div className="products-api-message">
            Chargement des produits...
          </div>
        )}

        {error && (
          <div className="products-api-error" role="alert">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* ONGLETS DES 4 CATÉGORIES */}
            <div
              className="category-tabs"
              role="tablist"
              aria-label="Catégories de produits"
            >
              {visibleCategories.map((category) => {
                const Icon = category.icon;
                const isActive =
                  category.name === selectedName;

                return (
                  <button
                    key={category.name}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`category-tab ${
                      isActive ? "active" : ""
                    }`}
                    onClick={() =>
                      handleCategoryChange(category.name)
                    }
                  >
                    <Icon />
                    <span>{category.title}</span>
                    <span className="tab-count">
                      {category.count}
                    </span>
                  </button>
                );
              })}
            </div>

            {selectedCategory ? (
              <div className="category" ref={resultsRef}>
                <div className="category-heading">
                  <h2>{selectedCategory.title}</h2>

                  <span className="product-count">
                    {categoryProducts.length} produit(s)
                  </span>
                </div>

                <div className="products-grid">
                  {displayedProducts.map((product) => (
                    <div
                      key={product.id}
                      className="product-card"
                    >
                      {product.image_url && (
                        <img
                          src={product.image_url}
                          alt={
                            product.name ||
                            "Produit Étoile Bricolage"
                          }
                          loading="lazy"
                          onError={(e) => {
                            e.currentTarget.style.display =
                              "none";
                          }}
                        />
                      )}

                      {product.name?.trim() && (
                        <p className="product-name">
                          {product.name}
                        </p>
                      )}

                      {product.description?.trim() && (
                        <p className="product-ref">
                          {product.description}
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                {categoryProducts.length === 0 && (
                  <div className="products-api-message">
                    Aucun produit dans cette catégorie.
                  </div>
                )}

                {/* PAGINATION */}
                {totalPages > 1 && (
                  <nav
                    className="products-pagination"
                    aria-label="Pagination des produits"
                  >
                    <button
                      type="button"
                      disabled={safePage === 1}
                      onClick={() =>
                        handlePageChange(safePage - 1)
                      }
                    >
                      Précédent
                    </button>

                    {getPageNumbers().map((page, index) =>
                      page === "..." ? (
                        <span
                          className="pagination-dots"
                          key={`dots-${index}`}
                        >
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          type="button"
                          className={
                            safePage === page ? "active" : ""
                          }
                          aria-current={
                            safePage === page
                              ? "page"
                              : undefined
                          }
                          onClick={() =>
                            handlePageChange(page)
                          }
                        >
                          {page}
                        </button>
                      )
                    )}

                    <button
                      type="button"
                      disabled={safePage === totalPages}
                      onClick={() =>
                        handlePageChange(safePage + 1)
                      }
                    >
                      Suivant
                    </button>
                  </nav>
                )}
              </div>
            ) : (
              <div className="products-api-message">
                Aucun produit trouvé pour votre recherche.
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}

export default Products;
